import { useRef, useState } from "react";
import { Button, Popover } from "@datapay-by-datacom/design-system";
import classnames from "classnames";
import {
  Source,
  SourceOrgCategory,
  SourceStatus,
  SourceType,
} from "../../../generated/ingest-api";
import { useDocumentDownload } from "../../hooks/OrgDocument/useDocumentDownload";
import { useDeleteSource } from "../../hooks/OrgSource/useDeleteSource";
import { useUpdateSource } from "../../hooks/OrgSource/useUpdateSource";
import { SourceVersionsPanel } from "./SourceVersionsPanel";
import styles from "./SourceRow.module.css";

interface SourceRowProps {
  source: Source;
  category: SourceOrgCategory;
  orgId: string;
  isAdmin: boolean;
  manualDocumentId?: string;
}

const statusStyleMap: Record<SourceStatus, string> = {
  [SourceStatus.active]: styles.badgeActive,
  [SourceStatus.paused]: styles.badgePaused,
  [SourceStatus.stale]: styles.badgeStale,
  [SourceStatus.retired]: styles.badgeRetired,
  [SourceStatus.error]: styles.badgeError,
};

function formatSourceType(type: SourceType): string {
  switch (type) {
    case SourceType.legislation_nz_pco:
      return "NZ Legislation";
    case SourceType.http_etag:
      return "HTTP ETag";
    case SourceType.manual:
      return "Manual";
    default:
      return type;
  }
}

export function SourceRow({
  source,
  category,
  orgId,
  isAdmin,
  manualDocumentId,
}: SourceRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuTriggerRef = useRef(null);
  const deleteMutation = useDeleteSource(category, orgId);
  const updateMutation = useUpdateSource(category, orgId);
  const columnCount = isAdmin ? 7 : 6;

  const isExpandable = source.source_type === SourceType.legislation_nz_pco;
  const sourceId = source.source_id ?? "";
  const canOpenManualDocument =
    isAdmin &&
    source.source_type === SourceType.manual &&
    manualDocumentId !== undefined;
  const { isLoading: isOpeningDocument, handleDocumentClick } =
    useDocumentDownload(category, orgId, manualDocumentId ?? "", source.source_name);

  const handleTogglePause = () => {
    const newStatus =
      source.source_status === SourceStatus.paused
        ? SourceStatus.active
        : SourceStatus.paused;

    updateMutation.mutate({
      sourceId,
      sourceUpdate: {
        source_id: sourceId,
        source_type: source.source_type,
        source_name: source.source_name,
        source_url: source.source_url,
        legislation_work_id: source.legislation_work_id,
        legislation_version_id: source.legislation_version_id,
        poll_interval_hours: source.poll_interval_hours,
        source_status: newStatus,
        org_category: source.org_category,
        org_id: source.org_id,
      },
    });
  };

  const handleDelete = () => {
    setIsMenuOpen(false);
    if (window.confirm(`Delete source "${source.source_name}"?`)) {
      deleteMutation.mutate(sourceId);
    }
  };

  const handleRowClick = () => {
    if (isExpandable) {
      setIsExpanded((prev) => !prev);
    }
  };

  const isPaused = source.source_status === SourceStatus.paused;

  return (
    <>
      <tr
        className={classnames(styles.row, {
          [styles.expandableRow]: isExpandable,
        })}
        onClick={handleRowClick}
      >
        <td>
          {canOpenManualDocument ? (
            <a
              href="#"
              onClick={handleDocumentClick}
              className={styles.documentLink}
              title="Open this document"
              aria-disabled={isOpeningDocument}
            >
              {source.source_name}
            </a>
          ) : (
            source.source_name
          )}
        </td>
        <td>
          <span className={styles.typeBadge}>
            {formatSourceType(source.source_type)}
          </span>
        </td>
        <td>
          <span
            className={`${styles.badge} ${statusStyleMap[source.source_status ?? SourceStatus.active] ?? ""}`}
          >
            {source.source_status ?? "active"}
          </span>
        </td>
        <td>
          {source.last_checked_at
            ? new Date(source.last_checked_at).toLocaleString()
            : "Never"}
        </td>
        <td>
          {source.last_changed_at
            ? new Date(source.last_changed_at).toLocaleString()
            : "-"}
        </td>
        {isAdmin && (
          <td>
            <div
              className={styles.actionsCell}
              onClick={(e) => e.stopPropagation()}
            >
              {source.source_type !== SourceType.manual ? (
                <>
                  <div className={styles.splitButton}>
                    <Button
                      variant="secondary"
                      size="small"
                      onPress={handleTogglePause}
                      isDisabled={updateMutation.isPending}
                    >
                      {updateMutation.isPending
                        ? "..."
                        : isPaused
                          ? "Resume"
                          : "Pause"}
                    </Button>
                    <button
                      ref={menuTriggerRef}
                      className={styles.splitDropdownTrigger}
                      onClick={() => setIsMenuOpen((prev) => !prev)}
                      aria-label="More actions"
                    >
                      &#9662;
                    </button>
                  </div>
                  <Popover
                    triggerRef={menuTriggerRef}
                    isOpen={isMenuOpen}
                    onOpenChange={setIsMenuOpen}
                    placement="bottom end"
                  >
                    <div className={styles.dropdownMenu}>
                      <Button
                        variant="tertiary"
                        size="small"
                        onPress={handleDelete}
                        isDisabled={deleteMutation.isPending}
                      >
                        {deleteMutation.isPending ? "Deleting..." : "Delete"}
                      </Button>
                    </div>
                  </Popover>
                </>
              ) : (
                <Button
                  variant="secondary"
                  size="small"
                  onPress={handleDelete}
                  isDisabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? "Deleting..." : "Delete"}
                </Button>
              )}
            </div>
          </td>
        )}
        <td className={styles.expandCell}>
          {isExpandable && (
            <span
              className={classnames(styles.expandIcon, {
                [styles.expandIconOpen]: isExpanded,
              })}
              title="View versions"
            >
              &#9654;
            </span>
          )}
        </td>
      </tr>
      {isExpandable && isExpanded && source.legislation_work_id && (
        <tr className={styles.versionsPanelRow}>
          <td colSpan={columnCount}>
            <SourceVersionsPanel
              category={category}
              orgId={orgId}
              docId={source.legislation_work_id}
              isAdmin={isAdmin}
            />
          </td>
        </tr>
      )}
    </>
  );
}
